import { apiClient } from "@/api/client";
import { endpoints } from "@/api/endpoints";
import type { ApiResponse } from "@/api/types";
import type {
  AuthResponse,
  AuthUser,
  ChangePasswordPayload,
  ConfirmChangeEmailPayload,
  LoginPayload,
  RequestChangeEmailPayload,
  SendOtpPayload,
  SendOtpResponse,
  UpdateProfilePayload,
  VerifyOtpPayload,
  VerifyOtpResponse,
} from "@/features/auth/types";

/**
 * All auth HTTP calls live here. Components and hooks never touch axios
 * directly — they call these functions instead.
 */
export const authService = {
  /**
   * `POST /auth/login` sets the session as httpOnly cookies and returns
   * `{ success, data: { user } }` — no token in the body.
   */
  async login(payload: LoginPayload): Promise<AuthResponse> {
    const { data } = await apiClient.post<ApiResponse<{ user: AuthUser }>>(endpoints.auth.login, payload);
    return { user: data.data.user };
  },

  // register() removed with POST /auth/register. That route was @Public() and
  // created an Active STAFF account with no invite, verification or approval.
  // It was never called — this method and its useRegister hook were dead.
  // Staff are created by an admin; client self-signup is leadService.

  async logout(): Promise<void> {
    await apiClient.post(endpoints.auth.logout);
  },

  async getMe(): Promise<AuthUser> {
    const { data } = await apiClient.get<ApiResponse<AuthUser>>(endpoints.auth.me);
    return data.data;
  },

  // ------------------------------------------------------------------
  // Phone OTP Auth
  // ------------------------------------------------------------------

  /**
   * `POST /auth/send-otp` — backend delivers OTP via WhatsApp/SMS and
   * enforces the resend cooldown per phone number.
   */
  async sendOtp(payload: SendOtpPayload): Promise<SendOtpResponse> {
    const { data } = await apiClient.post<ApiResponse<SendOtpResponse>>(
      endpoints.auth.sendOtp,
      payload,
    );
    return data.data;
  },

  /**
   * `POST /auth/verify-otp` — backend verifies the OTP code.
   *   • intent=LOGIN + existing user → session cookies set, returns { status: "LOGGED_IN", user }
   *   • intent=REGISTER (or new phone) → Redis marker set, returns { status: "VERIFIED_NEW_PHONE", phone }
   */
  async verifyOtp(payload: VerifyOtpPayload): Promise<VerifyOtpResponse> {
    const { data } = await apiClient.post<ApiResponse<VerifyOtpResponse>>(
      endpoints.auth.verifyOtp,
      payload,
    );
    return data.data;
  },

  /**
   * Reset runs over WhatsApp, not email — no transactional email provider is
   * configured, and the old emailed-link flow never actually sent anything.
   * `phone` is E.164, e.g. "+919876543210".
   */
  async forgotPassword(phone: string, extra: { website?: string } = {}): Promise<void> {
    await apiClient.post(endpoints.auth.forgotPassword, { phone, ...extra });
  },

  /**
   * Set the first password from an invite link. The token IS the credential —
   * the person has no password yet, which is why this route is public.
   */
  async acceptInvite(token: string, password: string): Promise<void> {
    await apiClient.post(endpoints.auth.acceptInvite, { token, password });
  },

  async resetPassword(phone: string, code: string, newPassword: string): Promise<void> {
    await apiClient.post(endpoints.auth.resetPassword, { phone, code, newPassword });
  },

  async updateProfile(payload: UpdateProfilePayload): Promise<AuthUser> {
    const { data } = await apiClient.patch<ApiResponse<AuthUser>>(endpoints.auth.profile, payload);
    return data.data;
  },

  async changePassword(payload: ChangePasswordPayload): Promise<{ message: string }> {
    const { data } = await apiClient.post<ApiResponse<{ message: string }>>(
      endpoints.auth.changePassword,
      payload,
    );
    return data.data;
  },

  async requestChangeEmail(payload: RequestChangeEmailPayload): Promise<{ pendingEmail: string }> {
    const { data } = await apiClient.post<ApiResponse<{ pendingEmail: string }>>(
      endpoints.auth.changeEmailRequest,
      payload,
    );
    return data.data;
  },

  async confirmChangeEmail(payload: ConfirmChangeEmailPayload): Promise<AuthUser> {
    const { data } = await apiClient.post<ApiResponse<AuthUser>>(
      endpoints.auth.changeEmailConfirm,
      payload,
    );
    return data.data;
  },

  async uploadAvatar(file: File): Promise<{ url: string }> {
    const form = new FormData();
    form.append("file", file);
    const { data } = await apiClient.post<ApiResponse<{ url: string }>>(
      endpoints.uploads.avatar,
      form,
      { headers: { "Content-Type": "multipart/form-data" } },
    );
    return data.data;
  },
};
